/* ============================================================================
   ORIPAYA · SAKURA COLLECTORS CLUB — COMPORTEMENT DU PANIER
   assets/oripaya-panier.js

   Pilote sections/oripaya-panier-drawer.liquid et
   sections/oripaya-panier-page.liquid.

   PRINCIPES
   ---------
   · Progressive enhancement stricte. Tout le panier est un <form> natif
     action="/cart" method="post". Ce fichier ne fait qu'éviter des
     rechargements ; s'il ne s'exécute pas, la boutique reste utilisable.
   · Aucun fichier du thème Nexus n'est modifié. Les comportements natifs
     concurrents (le <product-form> et le <cart-drawer> de critical.js) sont
     court-circuités en phase de CAPTURE au niveau du document : l'événement
     n'atteint jamais leurs écouteurs, donc aucun double ajout.
   · Le passage en caisse n'est JAMAIS intercepté : ni le bouton
     name="checkout", ni les boutons de paiement accéléré de Shopify.
   · Les mises à jour passent par l'API de rendu de section, sur l'identifiant
     réel de la section : le même code fonctionne que la section soit posée
     dans layout/theme.liquid ou dans un groupe de sections.

   SOMMAIRE
     1. Garde d'exécution unique et utilitaires
     2. Configuration
     3. Zones rafraîchissables
     4. Rendu : application des sections, compteur d'en-tête
     5. Tiroir : ouverture, fermeture, piège de focus, inert
     6. Statut : confirmation, erreur, annonce vocale
     7. Ajout au panier
     8. Quantité et retrait de ligne
     9. Recommandations différées
    10. Amorçage et éditeur de thème
   ============================================================================ */

(function () {
  'use strict';

  /* ==========================================================================
     1. GARDE D'EXÉCUTION UNIQUE ET UTILITAIRES
     ========================================================================== */

  // Les deux sections incluent ce script : le navigateur l'exécuterait deux
  // fois. Un drapeau global suffit, aucune duplication d'écouteur.
  if (window.__oripayaPanier) {
    window.__oripayaPanier.rescanner();
    return;
  }

  var SEL_ZONE = '[data-op-panier-section]';
  var CLASSE_SCROLL = 'sk-scroll-bloque';
  var CLASSE_ACTIF = 'op-panier-actif';

  function $(sel, racine) {
    return (racine || document).querySelector(sel);
  }
  function $$(sel, racine) {
    return Array.prototype.slice.call((racine || document).querySelectorAll(sel));
  }

  // Analyse une réponse HTML de l'API de rendu de section sans exécuter son
  // JavaScript ni charger ses images : DOMParser produit un document inerte.
  function analyser(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function mouvementReduit() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Lenis (défilement lissé du thème) doit être arrêté pendant qu'un tiroir
  // est ouvert, sinon la page continue de glisser derrière le voile.
  function lenis(action) {
    try {
      if (typeof window.whenLenisReady === 'function') {
        window.whenLenisReady(function (instance) {
          if (instance && typeof instance[action] === 'function') instance[action]();
        });
      }
    } catch (e) {
      /* le thème peut ne pas avoir chargé Lenis : ce n'est pas une erreur */
    }
  }

  function differer(fn) {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(fn, { timeout: 1200 });
    } else {
      setTimeout(fn, 240);
    }
  }

  /* ==========================================================================
     2. CONFIGURATION
     ========================================================================== */

  var CONFIG_DEFAUT = {
    sectionId: '',
    template: '',
    routeRacine: '/',
    routePanier: '/cart',
    intercepterAjouts: true,
    ouvrirApresAjout: true,
    intercepterLienPanier: true,
    neutraliserTiroirTheme: true,
    confirmationDuree: 5000,
    recosActif: true,
    recosNombre: 3,
    textes: {
      ajoute: 'Ajouté à votre panier.',
      voirPanier: 'Voir le panier',
      erreur: 'Une erreur est survenue. Veuillez réessayer.',
      chargement: 'Chargement…'
    }
  };

  var config = CONFIG_DEFAUT;

  function lireConfig() {
    // Le tiroir fait autorité : c'est lui qui porte les réglages de
    // comportement. La page panier ne fournit qu'un repli (routes, libellés)
    // pour le cas où elle est seule sur le gabarit « cart ».
    var balise = $('[data-op-config="drawer"]') || $('[data-op-config]');
    if (!balise) return CONFIG_DEFAUT;
    try {
      var brut = JSON.parse(balise.textContent);
      var fusion = {};
      var cle;
      for (cle in CONFIG_DEFAUT) {
        if (Object.prototype.hasOwnProperty.call(CONFIG_DEFAUT, cle)) {
          fusion[cle] = brut[cle] === undefined || brut[cle] === null ? CONFIG_DEFAUT[cle] : brut[cle];
        }
      }
      fusion.textes = Object.assign({}, CONFIG_DEFAUT.textes, brut.textes || {});
      return fusion;
    } catch (e) {
      return CONFIG_DEFAUT;
    }
  }

  function racineUrl() {
    // routes.root_url vaut '/' ou '/fr'. On normalise sans barre finale pour
    // pouvoir concaténer proprement.
    return String(config.routeRacine || '/').replace(/\/$/, '');
  }

  /* ==========================================================================
     3. ZONES RAFRAÎCHISSABLES
     ==========================================================================
     Une zone = un élément portant data-op-panier-section (l'identifiant réel
     de la section) et data-op-panier-racine (le sélecteur de l'élément dont
     on remplace le contenu). Le tiroir et la page panier sont deux zones du
     même mécanisme.
     ====================================================================== */

  var zones = [];

  function scannerZones() {
    zones = $$(SEL_ZONE).map(function (el) {
      return {
        el: el,
        id: el.getAttribute('data-op-panier-section'),
        racineSel: el.getAttribute('data-op-panier-racine') || ':scope > *'
      };
    });
    return zones;
  }

  function racineDeZone(zone) {
    return $(zone.racineSel, zone.el) || zone.el;
  }

  // Liste des sections à demander au serveur. On n'ajoute les sections du
  // thème que si elles sont réellement présentes ET encore actives : les
  // demander pour rien coûterait un rendu serveur à chaque clic.
  function sectionsDemandees() {
    var ids = zones
      .map(function (z) {
        return z.id;
      })
      .filter(Boolean);
    ids.push('cart-count');
    if (!config.neutraliserTiroirTheme && document.querySelector('cart-drawer')) ids.push('cart-drawer');
    if (document.querySelector('cart-main')) ids.push('cart-main');
    return ids
      .filter(function (id, i, tab) {
        return tab.indexOf(id) === i;
      })
      .join(',');
  }

  /* ==========================================================================
     4. RENDU
     ========================================================================== */

  function appliquerSections(sections) {
    if (!sections) return;

    zones.forEach(function (zone) {
      var html = sections[zone.id];
      if (!html) return;
      var doc = analyser(html);
      var neuf = $(zone.racineSel, doc);
      var actuel = racineDeZone(zone);
      if (!neuf || !actuel) return;

      // Le focus peut se trouver dans un morceau qu'on remplace (un bouton de
      // quantité, par exemple). On le mémorise pour ne pas le perdre.
      var avaitFocus = actuel.contains(document.activeElement);
      actuel.innerHTML = neuf.innerHTML;

      // Report des attributs de données utiles portés par la racine elle-même
      // (produit de référence pour les recommandations, totaux).
      ['data-op-reco-produit', 'data-op-total', 'data-op-compte'].forEach(function (attr) {
        var v = neuf.getAttribute(attr);
        if (v !== null) actuel.setAttribute(attr, v);
      });

      if (avaitFocus) {
        var panneau = actuel.matches('.sk-drawer__panneau') ? actuel : $('.sk-drawer__panneau', zone.el);
        if (panneau) panneau.focus({ preventScroll: true });
      }
    });

    majCompteurEntete(sections['cart-count']);

    // Compatibilité : les composants natifs de Nexus écoutent 'cart-update'.
    // On ne l'émet que si les sections qu'ils lisent sont bien présentes,
    // sinon leur code lèverait une exception sur un querySelector nul.
    var besoinDrawer = !!document.querySelector('cart-drawer') && !config.neutraliserTiroirTheme;
    var besoinMain = !!document.querySelector('cart-main');
    if ((!besoinDrawer || sections['cart-drawer']) && (!besoinMain || sections['cart-main'])) {
      try {
        document.dispatchEvent(new CustomEvent('cart-update', { detail: { sections: sections }, bubbles: true }));
      } catch (e) {
        /* un écouteur tiers en échec ne doit pas casser notre mise à jour */
      }
    }

    // Événement propre à ORIPAYA, sans dépendance au thème.
    document.dispatchEvent(new CustomEvent('oripaya:panier:maj', { detail: { sections: sections }, bubbles: true }));

    differer(function () {
      zones.forEach(chargerRecos);
    });
  }

  // Le compteur de l'en-tête est du balisage du thème : on remplace uniquement
  // le contenu de .cart-count-text, exactement comme le fait Nexus, sans
  // toucher à l'icône ni au lien.
  function majCompteurEntete(htmlCartCount) {
    var cibles = $$('.cart-count-text');
    if (!cibles.length) return;

    var nombre = null;
    if (htmlCartCount) {
      var doc = analyser(htmlCartCount);
      var source = $('.cart-count-text', doc);
      if (source) {
        cibles.forEach(function (cible) {
          cible.innerHTML = source.innerHTML;
        });
        var texte = $('.cc_text', source);
        if (texte) nombre = parseInt(texte.textContent, 10);
      }
    }

    if (nombre === null || isNaN(nombre)) return;
    cibles.forEach(function (cible) {
      // Nexus masque la pastille quand le panier est vide.
      cible.classList.toggle('visually--hidden', nombre <= 0);
    });
  }

  /* ==========================================================================
     5. TIROIR
     ========================================================================== */

  var drawer = {
    racine: null, // <oripaya-panier>
    el: null, // .sk-drawer
    ouvert: false,
    declencheur: null,
    inertes: [],
    minuteurFermeture: null
  };

  var SEL_FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function lierDrawer() {
    drawer.racine = $('oripaya-panier');
    drawer.el = drawer.racine ? $('.sk-drawer', drawer.racine) : null;
    if (drawer.el && config.neutraliserTiroirTheme && config.intercepterAjouts) {
      document.documentElement.classList.add(CLASSE_ACTIF);
    }

    // Coordination avec assets/oripaya-interactions.js : son module de
    // confirmation flottante s'active quand aucun tiroir panier n'est
    // détecté — or le nôtre n'est pas un <cart-drawer>. On utilise son
    // interrupteur public plutôt que de modifier son fichier.
    if (drawer.el && config.ouvrirApresAjout && window.ORIPAYA_CONFIRMATION === undefined) {
      window.ORIPAYA_CONFIRMATION = false;
    }
  }

  function focusables() {
    if (!drawer.el) return [];
    return $$(SEL_FOCUSABLE, drawer.el).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function poserInert() {
    drawer.inertes = [];
    if (!drawer.racine) return;
    var noeud = drawer.racine;
    while (noeud.parentElement && noeud !== document.body) {
      var parent = noeud.parentElement;
      Array.prototype.forEach.call(parent.children, function (enfant) {
        if (enfant === noeud) return;
        if (enfant.hasAttribute('inert')) return;
        var t = enfant.tagName;
        if (t === 'SCRIPT' || t === 'STYLE' || t === 'LINK' || t === 'TEMPLATE') return;
        enfant.setAttribute('inert', '');
        drawer.inertes.push(enfant);
      });
      noeud = parent;
    }
  }

  function retirerInert() {
    drawer.inertes.forEach(function (el) {
      el.removeAttribute('inert');
    });
    drawer.inertes = [];
  }

  function ouvrirDrawer(declencheur) {
    if (!drawer.el || drawer.ouvert) {
      if (drawer.ouvert) chargerRecosDuDrawer();
      return;
    }
    if (drawer.minuteurFermeture) {
      clearTimeout(drawer.minuteurFermeture);
      drawer.minuteurFermeture = null;
    }

    drawer.declencheur = declencheur || document.activeElement;
    drawer.ouvert = true;
    drawer.el.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add(CLASSE_SCROLL);
    poserInert();
    lenis('stop');

    var panneau = $('.sk-drawer__panneau', drawer.el);
    if (panneau) panneau.focus({ preventScroll: true });

    chargerRecosDuDrawer();
  }

  function fermerDrawer(rendreFocus) {
    if (!drawer.el || !drawer.ouvert) return;
    drawer.ouvert = false;
    drawer.el.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove(CLASSE_SCROLL);
    retirerInert();
    lenis('start');

    if (rendreFocus !== false && drawer.declencheur && document.contains(drawer.declencheur)) {
      try {
        drawer.declencheur.focus({ preventScroll: true });
      } catch (e) {
        /* le déclencheur a pu disparaître avec un rafraîchissement */
      }
    }
    drawer.declencheur = null;
  }

  // Piège de focus. On reste sur Tab/Maj+Tab plutôt que sur un focusin global :
  // le focusin capture aussi les clics et empêcherait la sélection de texte.
  document.addEventListener('keydown', function (e) {
    if (!drawer.ouvert || !drawer.el) return;

    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      fermerDrawer(true);
      return;
    }
    if (e.key !== 'Tab') return;

    var liste = focusables();
    if (!liste.length) {
      e.preventDefault();
      return;
    }
    var premier = liste[0];
    var dernier = liste[liste.length - 1];
    var actif = document.activeElement;

    if (e.shiftKey && (actif === premier || !drawer.el.contains(actif))) {
      e.preventDefault();
      dernier.focus();
    } else if (!e.shiftKey && actif === dernier) {
      e.preventDefault();
      premier.focus();
    }
  });

  // Fermeture : voile, croix, « continuer mes achats ».
  document.addEventListener('click', function (e) {
    var fermeur = e.target.closest ? e.target.closest('[data-sk-fermer]') : null;
    if (!fermeur || !drawer.el || !drawer.el.contains(fermeur)) return;
    e.preventDefault();
    fermerDrawer(true);
  });

  /* --------------------------------------------------------------------------
     Ouverture depuis l'en-tête. En phase de CAPTURE : le gestionnaire de
     tiroirs de Nexus est branché sur l'élément lui-même, donc en phase de
     bouillonnement. stopPropagation ici l'empêche d'ouvrir son propre tiroir.
     -------------------------------------------------------------------------- */
  document.addEventListener(
    'click',
    function (e) {
      if (!drawer.el || !config.intercepterLienPanier) return;
      if (e.defaultPrevented) return;
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      var cible = e.target.closest ? e.target.closest('a[href], [data-op-ouvrir-panier]') : null;
      if (!cible) return;
      if (drawer.el.contains(cible)) return;

      var explicite = cible.hasAttribute('data-op-ouvrir-panier');
      if (!explicite) {
        // Sur la page panier, le lien doit rester un lien.
        if (config.template === 'cart') return;
        if (cible.getAttribute('target') === '_blank') return;
        var url;
        try {
          url = new URL(cible.href, window.location.origin);
        } catch (err) {
          return;
        }
        if (url.origin !== window.location.origin) return;
        if (url.search) return; // /cart?view=…, permaliens : on ne touche pas
        if (!/\/cart\/?$/.test(url.pathname)) return;
      }

      e.preventDefault();
      e.stopPropagation();
      ouvrirDrawer(cible);
    },
    true
  );

  /* ==========================================================================
     6. STATUT
     ========================================================================== */

  var SVG_SUCCES =
    '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">' +
    '<circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="1.3"/>' +
    '<path d="m6.5 10.2 2.4 2.4 4.6-5.2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  var SVG_ERREUR =
    '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">' +
    '<circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="1.3"/>' +
    '<path d="M10 6v5M10 13.6v.6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

  var minuteurStatut = null;

  function annoncer(texte) {
    var annonce = $('[data-op-annonce]');
    if (!annonce) return;
    // Vider puis réécrire force la relecture même si le texte est identique.
    annonce.textContent = '';
    window.setTimeout(function () {
      annonce.textContent = texte;
    }, 60);
  }

  function afficherStatut(type, titre, detail, duree) {
    annoncer(titre + (detail ? ' ' + detail : ''));

    var boites = $$('[data-op-statut]');
    if (!boites.length) return;

    var classe = type === 'erreur' ? 'sk-etat-erreur' : 'sk-etat-succes';
    var icone = type === 'erreur' ? SVG_ERREUR : SVG_SUCCES;
    var html =
      '<div class="' + classe + '">' +
      '<span class="' + classe + '__icone" aria-hidden="true">' + icone + '</span>' +
      '<span><span class="op-statut__titre">' + echapper(titre) + '</span>' +
      (detail ? '<br>' + echapper(detail) : '') +
      '</span></div>';

    boites.forEach(function (boite) {
      var contenu = $('[data-op-statut-contenu]', boite);
      if (!contenu) return;
      contenu.innerHTML = html;
      boite.hidden = false;
      // Un frame de battement : sans lui, la transition de grid-template-rows
      // ne démarre pas puisque l'élément vient d'apparaître.
      window.requestAnimationFrame(function () {
        boite.setAttribute('data-op-visible', 'true');
      });
    });

    if (minuteurStatut) clearTimeout(minuteurStatut);
    if (duree !== 0) {
      minuteurStatut = window.setTimeout(masquerStatut, duree || config.confirmationDuree);
    }
  }

  function masquerStatut() {
    $$('[data-op-statut]').forEach(function (boite) {
      boite.removeAttribute('data-op-visible');
      var delai = mouvementReduit() ? 0 : 400;
      window.setTimeout(function () {
        if (!boite.hasAttribute('data-op-visible')) boite.hidden = true;
      }, delai);
    });
  }

  function echapper(s) {
    var d = document.createElement('span');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  }

  /* --- Confirmation hors tiroir : petite bannière, jamais bloquante -------- */

  var toast = null;
  var minuteurToast = null;

  function afficherToast(texte) {
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'op-toast';
      toast.setAttribute('role', 'status');
      document.body.appendChild(toast);
    }
    toast.innerHTML =
      echapper(texte) +
      '<br><a class="op-toast__lien" href="' +
      echapper(config.routePanier) +
      '" data-op-ouvrir-panier>' +
      echapper(config.textes.voirPanier) +
      '</a>';
    window.requestAnimationFrame(function () {
      toast.setAttribute('data-op-visible', 'true');
    });
    if (minuteurToast) clearTimeout(minuteurToast);
    minuteurToast = window.setTimeout(function () {
      if (toast) toast.removeAttribute('data-op-visible');
    }, config.confirmationDuree);
  }

  /* ==========================================================================
     7. AJOUT AU PANIER
     ==========================================================================
     On intercepte en phase de capture au niveau du document. Le <product-form>
     de Nexus écoute le submit sur le formulaire lui-même (donc plus tard) :
     stopPropagation garantit qu'il n'y aura pas de second appel à /cart/add.
     ====================================================================== */

  function estFormulaireAjout(form) {
    var action = form.getAttribute('action') || '';
    return /\/cart\/add/.test(action);
  }

  function doitIgnorer(form, submitter) {
    if (form.hasAttribute('data-op-ignorer')) return true;
    if (!submitter) return false;
    if (submitter.name === 'checkout') return true;
    // Les boutons de paiement accéléré gèrent eux-mêmes leur soumission :
    // on ne s'interpose jamais entre l'acheteur et la caisse.
    if (submitter.closest('.shopify-payment-button, [data-shopify="payment-button"]')) return true;
    return false;
  }

  function construirePayload(form, submitter) {
    var donnees;
    try {
      donnees = new FormData(form, submitter);
    } catch (e) {
      donnees = new FormData(form);
    }

    var article = {};
    donnees.forEach(function (valeur, cle) {
      var propriete = cle.match(/^properties\[(.+)\]$/);
      if (propriete) {
        if (!article.properties) article.properties = {};
        article.properties[propriete[1]] = valeur;
      } else if (cle !== 'form_type' && cle !== 'utf8') {
        article[cle] = valeur;
      }
    });

    var articles = [article];

    // Parité avec le thème : l'emballage cadeau est une ligne enfant rattachée
    // au produit principal. Sans cette reprise, l'option disparaîtrait.
    var infoProduit = form.closest('product-info');
    var emballage = infoProduit ? $('#product_gift_wrapp', infoProduit) : null;
    var champId = $('input[name="id"]', form);
    if (emballage && emballage.checked && champId) {
      articles.push({
        id: emballage.value,
        quantity: 1,
        parent_id: champId.value,
        properties: {
          for: (champId.dataset.title || '') + ' (' + (champId.dataset.variantTitle || '') + ')'
        }
      });
    }

    return articles;
  }

  function etatBouton(form, occupe) {
    var bouton = $('.add_to_cart_button', form) || $('button[type="submit"]', form);
    if (!bouton) return null;
    bouton.setAttribute('aria-busy', occupe ? 'true' : 'false');
    // On n'utilise pas `disabled` : cela retirerait le focus du bouton et
    // ferait perdre sa place à l'utilisateur au clavier.
    var loader = $('.form_loader', bouton);
    var texte = $('.atc_text', bouton) || $('.icon_wrapper', bouton);
    if (loader) loader.classList.toggle('hidden', !occupe);
    if (texte && loader) texte.classList.toggle('hidden', occupe);
    return bouton;
  }

  function afficherErreurFormulaire(form, message) {
    var zone = $('.product_card_cart_error', form.parentElement || form) || $('.product_quantity_error', form.closest('product-info') || form);
    if (zone) {
      zone.innerHTML = echapper(message);
      zone.classList.add('show');
      zone.classList.remove('hidden');
      window.setTimeout(function () {
        zone.classList.remove('show');
        zone.classList.add('hidden');
      }, 4000);
    }
  }

  document.addEventListener(
    'submit',
    function (e) {
      if (!config.intercepterAjouts) return;
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      if (!estFormulaireAjout(form)) return;
      if (doitIgnorer(form, e.submitter)) return;
      if (form.dataset.opEnCours === '1') {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      form.dataset.opEnCours = '1';
      etatBouton(form, true);

      var articles = construirePayload(form, e.submitter);
      var titre = '';
      var champTitre = $('input[name="id"]', form);
      if (champTitre && champTitre.dataset.title) titre = champTitre.dataset.title;

      fetch(racineUrl() + '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: articles,
          sections: sectionsDemandees(),
          sections_url: window.location.pathname
        })
      })
        .then(function (reponse) {
          return reponse.json().then(function (donnees) {
            return { ok: reponse.ok, donnees: donnees };
          });
        })
        .then(function (res) {
          if (!res.ok) {
            var message = res.donnees.description || res.donnees.message || config.textes.erreur;
            afficherErreurFormulaire(form, message);
            afficherStatut('erreur', message, '', 6000);
            // Le panier peut avoir changé malgré l'erreur (article partiel) :
            // on resynchronise si le serveur nous a renvoyé les sections.
            if (res.donnees.sections) appliquerSections(res.donnees.sections);
            else rafraichir();
            return;
          }

          appliquerSections(res.donnees.sections);

          var libelle = config.textes.ajoute;
          if (config.ouvrirApresAjout && drawer.el) {
            ouvrirDrawer(document.activeElement === document.body ? null : document.activeElement);
            afficherStatut('succes', libelle, titre, config.confirmationDuree);
          } else {
            annoncer(libelle + ' ' + titre);
            afficherToast(libelle + (titre ? ' ' + titre : ''));
          }
        })
        .catch(function () {
          afficherErreurFormulaire(form, config.textes.erreur);
          afficherStatut('erreur', config.textes.erreur, '', 6000);
        })
        .then(function () {
          form.dataset.opEnCours = '';
          etatBouton(form, false);
        });
    },
    true
  );

  /* ==========================================================================
     8. QUANTITÉ ET RETRAIT DE LIGNE
     ==========================================================================
     État optimiste : le nombre affiché change tout de suite, la ligne se met
     en retrait, puis le serveur renvoie les sections. En cas d'échec, on
     restaure la valeur d'origine et on affiche le message SUR la ligne — pas
     dans une alerte globale que l'acheteur ne rattacherait à rien.
     ====================================================================== */

  // Les requêtes de panier sont sérialisées : deux /cart/change.js simultanés
  // se marchent dessus et Shopify n'en garantit pas l'ordre.
  var file = Promise.resolve();

  function enFile(fn) {
    file = file.then(fn).catch(function () {});
    return file;
  }

  function ligneDe(el) {
    return el.closest ? el.closest('[data-op-ligne]') : null;
  }

  function erreurLigne(ligne, message) {
    var p = $('[data-op-ligne-erreur]', ligne);
    if (!p) return;
    if (!message) {
      p.hidden = true;
      p.textContent = '';
      return;
    }
    p.textContent = message;
    p.hidden = false;
  }

  function changerQuantite(ligne, quantite) {
    if (!ligne) return;
    var cle = ligne.getAttribute('data-op-cle');
    if (!cle) return;

    var champ = $('[data-op-qte]', ligne);
    var precedente = parseInt(ligne.getAttribute('data-op-quantite'), 10) || 0;
    quantite = Math.max(0, parseInt(quantite, 10) || 0);
    if (quantite === precedente) return;

    erreurLigne(ligne, '');
    if (champ) champ.value = quantite;
    ligne.setAttribute('data-op-quantite', quantite);
    ligne.setAttribute(quantite === 0 ? 'data-op-retire' : 'data-op-occupe', 'true');

    var form = ligne.closest('[data-op-form]');
    if (form) form.setAttribute('aria-busy', 'true');

    enFile(function () {
      return fetch(racineUrl() + '/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: cle,
          quantity: quantite,
          sections: sectionsDemandees(),
          sections_url: window.location.pathname
        })
      })
        .then(function (reponse) {
          return reponse.json().then(function (donnees) {
            return { ok: reponse.ok, donnees: donnees };
          });
        })
        .then(function (res) {
          if (!res.ok) {
            // Retour en arrière : la ligne reprend sa quantité d'origine.
            ligne.removeAttribute('data-op-retire');
            ligne.removeAttribute('data-op-occupe');
            ligne.setAttribute('data-op-quantite', precedente);
            if (champ) champ.value = precedente;
            var message = res.donnees.description || res.donnees.message || config.textes.erreur;
            erreurLigne(ligne, message);
            annoncer(message);
            return;
          }
          appliquerSections(res.donnees.sections);
        })
        .catch(function () {
          ligne.removeAttribute('data-op-retire');
          ligne.removeAttribute('data-op-occupe');
          ligne.setAttribute('data-op-quantite', precedente);
          if (champ) champ.value = precedente;
          erreurLigne(ligne, config.textes.erreur);
        })
        .then(function () {
          // Après un rafraîchissement réussi le formulaire est détaché : le
          // test évite de poser un attribut sur un nœud mort.
          if (form && document.contains(form)) form.removeAttribute('aria-busy');
        });
    });
  }

  // Boutons − et +, et lien de retrait.
  document.addEventListener('click', function (e) {
    var cible = e.target.closest ? e.target : null;
    if (!cible) return;

    var moins = cible.closest('[data-op-moins]');
    var plus = cible.closest('[data-op-plus]');
    var retirer = cible.closest('[data-op-retirer]');
    if (!moins && !plus && !retirer) return;

    var ligne = ligneDe(moins || plus || retirer);
    if (!ligne) return;

    e.preventDefault();
    var actuelle = parseInt(ligne.getAttribute('data-op-quantite'), 10) || 0;
    if (retirer) changerQuantite(ligne, 0);
    else if (moins) changerQuantite(ligne, actuelle - 1);
    else changerQuantite(ligne, actuelle + 1);
  });

  // Saisie directe dans le champ. On écoute 'change' et non 'input' : il ne
  // faut pas envoyer une requête à chaque frappe.
  document.addEventListener('change', function (e) {
    var champ = e.target.closest ? e.target.closest('[data-op-qte]') : null;
    if (!champ) return;
    var ligne = ligneDe(champ);
    if (!ligne) return;
    changerQuantite(ligne, champ.value);
  });

  // Entrée dans le champ de quantité : on valide la ligne, on ne soumet pas
  // tout le formulaire (ce qui rechargerait la page).
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var champ = e.target.closest ? e.target.closest('[data-op-qte]') : null;
    if (!champ) return;
    e.preventDefault();
    champ.blur();
  });

  // Note de commande : enregistrée à la sortie du champ, sans rechargement.
  document.addEventListener(
    'blur',
    function (e) {
      var champ = e.target && e.target.closest ? e.target.closest('[data-op-note]') : null;
      if (!champ) return;
      if (champ.dataset.opValeur === champ.value) return;
      champ.dataset.opValeur = champ.value;
      enFile(function () {
        return fetch(racineUrl() + '/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: champ.value })
        }).catch(function () {});
      });
    },
    true
  );

  // Resynchronisation complète, utilisée en secours.
  function rafraichir() {
    var ids = sectionsDemandees();
    if (!ids) return Promise.resolve();
    return fetch(window.location.pathname + '?sections=' + encodeURIComponent(ids), {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) {
        return r.json();
      })
      .then(appliquerSections)
      .catch(function () {});
  }

  /* ==========================================================================
     9. RECOMMANDATIONS DIFFÉRÉES
     ==========================================================================
     /recommendations/products avec intent=complementary, trois articles au
     maximum, jamais au chargement de la page : uniquement quand le tiroir
     s'ouvre, ou quand la page panier est réellement lue.
     ====================================================================== */

  var SQUELETTE_RECOS =
    '<div class="op-recos__attente" aria-hidden="true">' +
    '<div class="op-recos__attente-item"><span class="sk-squelette sk-squelette--carre"></span>' +
    '<span class="op-recos__attente-texte"><span class="sk-squelette sk-squelette--titre"></span>' +
    '<span class="sk-squelette sk-squelette--texte-court"></span></span></div>' +
    '<div class="op-recos__attente-item"><span class="sk-squelette sk-squelette--carre"></span>' +
    '<span class="op-recos__attente-texte"><span class="sk-squelette sk-squelette--titre"></span>' +
    '<span class="sk-squelette sk-squelette--texte-court"></span></span></div>' +
    '</div>';

  function chargerRecos(zone) {
    if (!zone || !config.recosActif) return;
    var cible = $('[data-op-recos]', zone.el);
    if (!cible) return;

    var racine = racineDeZone(zone);
    var produit = racine ? racine.getAttribute('data-op-reco-produit') : null;
    if (!produit) {
      cible.hidden = true;
      return;
    }
    // Dans le tiroir, on ne dépense pas une requête tant qu'il est fermé.
    var tiroir = cible.closest('.sk-drawer');
    if (tiroir && tiroir.getAttribute('aria-hidden') !== 'false') return;

    if (cible.dataset.opRecoCharge === produit) return;
    cible.dataset.opRecoCharge = produit;

    // Squelette uniquement dans le tiroir : sur la page, un bloc fantôme en
    // bas de page n'apporte rien et allonge le document pour rien.
    if (cible.classList.contains('op-recos')) {
      cible.innerHTML = SQUELETTE_RECOS;
      cible.hidden = false;
      cible.setAttribute('aria-busy', 'true');
    }

    var limite = Math.min(4, parseInt(config.recosNombre, 10) || 3);
    var url =
      racineUrl() +
      '/recommendations/products?section_id=' +
      encodeURIComponent(zone.id) +
      '&product_id=' +
      encodeURIComponent(produit) +
      '&limit=' +
      limite +
      '&intent=complementary';

    fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (r) {
        return r.ok ? r.text() : '';
      })
      .then(function (html) {
        cible.removeAttribute('aria-busy');
        var fragment = html ? $('[data-op-recos-fragment]', analyser(html)) : null;
        if (fragment && fragment.children.length) {
          cible.innerHTML = fragment.innerHTML;
          cible.hidden = false;
        } else {
          cible.innerHTML = '';
          cible.hidden = true;
        }
      })
      .catch(function () {
        cible.removeAttribute('aria-busy');
        cible.innerHTML = '';
        cible.hidden = true;
      });
  }

  function chargerRecosDuDrawer() {
    if (!drawer.racine) return;
    var zone = zones.filter(function (z) {
      return z.el === drawer.racine || z.el.contains(drawer.racine) || drawer.racine.contains(z.el);
    })[0];
    if (zone) differer(function () {
      chargerRecos(zone);
    });
  }

  /* ==========================================================================
     10. AMORÇAGE ET ÉDITEUR DE THÈME
     ========================================================================== */

  function initialiser() {
    config = lireConfig();
    scannerZones();
    lierDrawer();

    // Sur la page panier, les recommandations se chargent quand la zone est
    // réellement approchée du champ de vision.
    var zonePage = zones.filter(function (z) {
      return z.el !== drawer.racine;
    })[0];
    if (zonePage && 'IntersectionObserver' in window) {
      var cible = $('[data-op-recos]', zonePage.el);
      if (cible) {
        var obs = new IntersectionObserver(
          function (entrees) {
            entrees.forEach(function (entree) {
              if (!entree.isIntersecting) return;
              obs.disconnect();
              chargerRecos(zonePage);
            });
          },
          { rootMargin: '400px 0px' }
        );
        obs.observe(cible);
      }
    }
  }

  window.__oripayaPanier = {
    ouvrir: ouvrirDrawer,
    fermer: fermerDrawer,
    rafraichir: rafraichir,
    rescanner: function () {
      config = lireConfig();
      scannerZones();
      lierDrawer();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiser);
  } else {
    initialiser();
  }

  // Éditeur de thème : les sections sont remplacées à chaud.
  document.addEventListener('shopify:section:load', function (e) {
    fermerDrawer(false);
    window.__oripayaPanier.rescanner();
    if (e.target && e.target.querySelector && e.target.querySelector('oripaya-panier')) {
      // Ouvrir le tiroir dès qu'il est sélectionné dans l'éditeur : sinon le
      // marchand ne verrait jamais ce qu'il règle.
      if (window.Shopify && window.Shopify.designMode) ouvrirDrawer(null);
    }
  });
  document.addEventListener('shopify:section:unload', function () {
    fermerDrawer(false);
    window.__oripayaPanier.rescanner();
  });
  document.addEventListener('shopify:section:select', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('oripaya-panier')) ouvrirDrawer(null);
  });
  document.addEventListener('shopify:section:deselect', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('oripaya-panier')) fermerDrawer(false);
  });
})();
